import type { EventBus } from '@core/bus/EventBus'
import { debugLog } from '@core/utils/log'
import { PosEvent } from '@core/bus/events'

/** Настройки детекции HID-сканера. */
export interface ScannerOptions {
  /**
   * Максимальный интервал между символами (мс), при котором ввод считается
   * «сканом», а не ручным набором. Аппаратный HID-сканер «печатает» очень
   * быстро. Дефолт 50 мс — безопасный порог для слабых CPU (Android 7): при
   * задержках рендеринга штрихкод не дробится на части. Параметр настраиваемый
   * (конструктор или setter) — задел под вынос в настройки кассы.
   */
  maxInterKeyMs?: number
  /**
   * Минимальная длина кода, чтобы считать его валидным сканом.
   * Штрихкоды EAN-13/UPC/GTIN длиннее 8 символов, поэтому «человеческие»
   * короткие нажатия в инпутах не превращаются в скан.
   */
  minLength?: number
}

const DEFAULT_OPTIONS: Required<ScannerOptions> = {
  maxInterKeyMs: 50,
  minLength: 8
}

/**
 * Глобальный слушатель аппаратных сканеров штрихкодов в режиме HID
 * (эмуляция клавиатуры).
 *
 * Как это работает:
 * - Вешаем keydown в режиме ЗАХВАТА (capture=true), чтобы перехватывать ввод
 *   раньше любых инпутов интерфейса кассы.
 * - Сканер «печатает» символы с очень малыми паузами и завершает ввод Enter.
 *   Если межсимвольный интервал < maxInterKeyMs — это сканер (fastStreak).
 *   Если пауза больше — это человек, буфер сбрасывается.
 * - По Enter при быстром потоке и длине буфера > minLength эмитим
 *   BARCODE_SCANNED через шину и чистим буфер.
 * - preventDefault/stopPropagation на символах быстрого потока и на
 *   завершающем Enter: штрихкод НЕ попадает в сфокусированный input кассы
 *   (иначе код «напечатался» бы в поле поиска/количества).
 */
export class ScannerService {
  private readonly opts: Required<ScannerOptions>
  private buffer = ''
  private lastKeyTime = 0
  private fastStreak = false
  private listening = false
  private readonly onKeyDown: (e: KeyboardEvent) => void

  constructor(
    private readonly bus: EventBus,
    options: ScannerOptions = {}
  ) {
    this.opts = { ...DEFAULT_OPTIONS, ...options }
    this.onKeyDown = this.handleKeyDown.bind(this)
  }

  /** Начать слушать клавиатуру (window keydown, capture). */
  start(): void {
    if (this.listening || typeof window === 'undefined') {
      return
    }
    window.addEventListener('keydown', this.onKeyDown, true)
    this.listening = true
    debugLog('[scanner] HID-слушатель запущен (capture)')
  }

  /** Прекратить слушать. */
  stop(): void {
    if (!this.listening) {
      return
    }
    window.removeEventListener('keydown', this.onKeyDown, true)
    this.listening = false
    this.reset()
  }

  get isListening(): boolean {
    return this.listening
  }

  /** Текущий порог детекции скана (мс). */
  get maxInterKeyMs(): number {
    return this.opts.maxInterKeyMs
  }

  /**
   * Изменить порог детекции скана во время работы (задел под настройки кассы).
   * Значения <= 0 игнорируются как некорректные.
   */
  setMaxInterKeyMs(ms: number): void {
    if (Number.isFinite(ms) && ms > 0) {
      this.opts.maxInterKeyMs = ms
      debugLog(`[scanner] порог детекции скана обновлён: ${ms} мс`)
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    const now = Date.now()
    const delta = now - this.lastKeyTime
    this.lastKeyTime = now

    if (e.key === 'Enter') {
      // Завершение кода: эмитим только если поток был «быстрым» (сканер)
      // и накопленная длина похожа на штрихкод (> minLength).
      if (this.fastStreak && this.buffer.length > this.opts.minLength) {
        const raw = this.buffer
        this.reset()
        // Гасим Enter, чтобы он не «сабмитил» форму кассы.
        e.preventDefault()
        e.stopPropagation()
        void this.bus.emit(PosEvent.BARCODE_SCANNED, { raw })
        debugLog('[scanner] BARCODE_SCANNED:', raw)
      } else {
        // Это, скорее всего, ручной Enter — буфер сбрасываем, событие не шлём.
        this.reset()
      }
      return
    }

    // Интересуют только печатаемые одиночные символы (цифры/буквы штрихкода).
    if (e.key.length !== 1) {
      return
    }

    if (this.buffer === '') {
      // Первый символ последовательности. Ещё НЕ знаем, сканер это или человек
      // (нет межсимвольного интервала), поэтому символ НЕ перехватываем —
      // чтобы не «проглотить» первое нажатие при ручном вводе.
      this.fastStreak = false
      this.buffer = e.key
      return
    }

    if (delta < this.opts.maxInterKeyMs) {
      // Быстрый интервал — поток похож на сканер.
      this.fastStreak = true
    } else {
      // Пауза «человеческая» — это ручной ввод. Начинаем буфер заново от
      // текущего символа и НЕ перехватываем (пусть печатается в инпут).
      this.fastStreak = false
      this.buffer = e.key
      return
    }

    this.buffer += e.key

    // Подтверждённый быстрый поток — не даём символам утечь в input.
    e.preventDefault()
    e.stopPropagation()
  }

  private reset(): void {
    this.buffer = ''
    this.fastStreak = false
  }
}
